/* ============================================================================
   FormKit tadmin — primitivas de formulario Yool (Apple-clean)
   Port pixel-perfect del rediseño de Claude Design (divina/FormKit.jsx +
   EditarPlanScreen/EditarProductoScreen). Solo capa visual: los call-sites
   conservan su estado y handlers.
   Tokens (FK_TOKENS): inputs 40px / borde 1.5px / radio 8px / focus teal.
   Botones: primary teal (1 por vista) · ghost · outline · danger outline · link.
   El teal (#007D6E) queda reservado al CTA.
   ============================================================================ */
import React, { useState } from "react";
import {
  ClipboardList, Wallet, Layers, Settings, Calculator, BarChart3, Percent,
  Shield, FileText, Info, Check, AlertTriangle, X, Download, ChevronLeft,
} from "lucide-react";
import { Button } from "../ui/button";
import { Switch, RadioPills } from "../ui/switch";
import { color, type, radius } from "../../styles/tokens/formkit";

/* ── Tokens (fuente única: styles/tokens/formkit → CSS vars de yool-admin.css) ── */
export const FK = color;
export { color, type, radius };

/* Componentes reales del design system (bundle public/ds/_ds_bundle.js → window
   .DesignSystemRemix_1a4bd3). Acceso LAZY: se lee window en cada render, así el
   orden de evaluación de módulos no importa. Si el bundle no cargó, cada
   adaptador cae a su fallback tokenizado (mismo look, medido del propio bundle). */
const DS = () => (typeof window !== "undefined" && window.DesignSystemRemix_1a4bd3) || {};

/* ── Íconos (mapea los nombres del mockup a lucide) ─────────────────────── */
const ICONS = {
  clipboard: ClipboardList, wallet: Wallet, layers: Layers, settings: Settings,
  calculator: Calculator, barChart: BarChart3, percent: Percent, shield: Shield,
  document: FileText, info: Info, check: Check, warn: AlertTriangle, x: X,
  close: X, download: Download, back: ChevronLeft,
};
export function YIcon({ name, size = 16, style, strokeWidth = 2 }) {
  const Cmp = ICONS[name] || FileText;
  return <Cmp size={size} style={style} strokeWidth={strokeWidth} />;
}

/* ── Botones (reusan ui/button; el teal es el único CTA) ─────────────────── */
export function FkPrimary({ children, onClick, type = "button", disabled }) {
  const { YoolButton } = DS();
  if (YoolButton) return <YoolButton variant="primary" type={type} onClick={onClick} disabled={disabled}>{children}</YoolButton>;
  return <Button variant="default" type={type} onClick={onClick} disabled={disabled}>{children}</Button>;
}
export function FkGhost({ children, onClick, type = "button", disabled }) {
  const { YoolButton } = DS();
  if (YoolButton) return <YoolButton variant="ghost" type={type} onClick={onClick} disabled={disabled}>{children}</YoolButton>;
  return <Button variant="ghost" type={type} onClick={onClick} disabled={disabled}>{children}</Button>;
}
export function FkOutline({ children, onClick, type = "button" }) {
  const { YoolButton } = DS();
  if (YoolButton) return <YoolButton variant="outline" type={type} onClick={onClick}>{children}</YoolButton>;
  return <Button variant="outline" type={type} onClick={onClick}>{children}</Button>;
}
export function FkDanger({ children, onClick, type = "button", disabled }) {
  const { YoolButton } = DS();
  if (YoolButton) return <YoolButton variant="danger-outline" type={type} onClick={onClick} disabled={disabled}>{children}</YoolButton>;
  return (
    <Button
      variant="outline"
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="border-[1.5px] border-[#ED4068] text-[#C0004A] hover:bg-[#C0004A]/5 hover:border-[#C0004A] dark:border-[#ED4068] dark:text-[#ED4068]"
    >
      {children}
    </Button>
  );
}
export function FkLink({ children, onClick, type = "button" }) {
  const { YoolButton } = DS();
  if (YoolButton) return <YoolButton variant="link" type={type} onClick={onClick}>{children}</YoolButton>;
  return <Button variant="link" type={type} onClick={onClick} className="px-0 h-auto gap-1.5">{children}</Button>;
}

/* ── Campo (label + control) ─────────────────────────────────────────────── */
export function Field({ label, required, hint, htmlFor, children, full }) {
  const { YoolField } = DS();
  if (YoolField) return <YoolField label={label} required={required} hint={hint} htmlFor={htmlFor} full={full}>{children}</YoolField>;
  return (
    <div style={{ flex: full ? "1 1 100%" : "1 1 240px", minWidth: full ? 0 : 220, display: "flex", flexDirection: "column", gap: 7 }}>
      {label && (
        <label htmlFor={htmlFor} style={{ display: "block", ...type.label }}>
          {label}{required && <span style={{ color: FK.errBorder, marginLeft: 3 }}>*</span>}
        </label>
      )}
      {children}
      {hint && <p style={{ margin: 0, ...type.hint }}>{hint}</p>}
    </div>
  );
}

/* ── Inputs (fieldBase: h40 / borde 1.5px / radio 8 / bg #FBFBFB / focus teal) ── */
function fieldBase(focused, extra) {
  return {
    width: "100%", boxSizing: "border-box", height: 40, padding: "0 12px", borderRadius: 8,
    border: "1.5px solid " + (focused ? FK.cta : FK.border), background: FK.field, color: FK.text,
    fontSize: 14, fontFamily: "inherit", outline: "none", transition: "border-color .15s ease",
    ...extra,
  };
}
export function TextInput({ id, name, value, onChange, placeholder, type = "text", upper, disabled, ...rest }) {
  const [f, setF] = useState(false);
  return (
    <input
      {...rest}
      id={id} name={name} type={type} value={value ?? ""} placeholder={placeholder} disabled={disabled}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
      onChange={(e) => onChange && onChange(e)}
      style={fieldBase(f, upper ? { textTransform: "uppercase", letterSpacing: "0.02em" } : null)}
    />
  );
}
export function TextArea({ id, name, value, onChange, placeholder, rows = 4, disabled }) {
  const [f, setF] = useState(false);
  return (
    <textarea
      id={id} name={name} value={value ?? ""} placeholder={placeholder} rows={rows} disabled={disabled}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
      onChange={(e) => onChange && onChange(e)}
      style={fieldBase(f, { height: "auto", padding: "10px 12px", lineHeight: 1.5, resize: "vertical", minHeight: 40 })}
    />
  );
}
export function Select({ id, name, value, onChange, options, placeholder, disabled }) {
  const [f, setF] = useState(false);
  const { YoolSelect } = DS();
  if (YoolSelect) return <YoolSelect id={id} value={value ?? ""} options={options} placeholder={placeholder} disabled={disabled} onChange={onChange} />;
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <select
        id={id} name={name} value={value ?? ""} disabled={disabled}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        onChange={(e) => onChange && onChange(e)}
        style={fieldBase(f, { appearance: "none", WebkitAppearance: "none", MozAppearance: "none", paddingRight: 34, cursor: "pointer" })}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: FK.muted, fontSize: 11 }}>▾</span>
    </div>
  );
}

/* ── Toggle y radios pill ─────────────────────────────────────────────────────
   Los dos delegan en `ui/switch.jsx`, que es donde vive la única copia del
   diseño (medidas y colores de `YoolToggle` / `YoolRadioGroup` del Yool DS).

   Antes cada uno preguntaba por el bundle y, si no estaba, dibujaba su propia
   versión: el toggle caía a un `Switch` teal de 44px y el radio a un pill con
   borde navy y fondo blanco. Ninguna de las dos era el DS, así que la pantalla
   cambiaba de aspecto según si el bundle había cargado. Ahora no hay segunda
   versión que mantener.
   @see components/ui/switch.jsx */
export function FkToggle({ on, onChange, disabled, ...rest }) {
  return <Switch checked={!!on} onCheckedChange={onChange} disabled={disabled} {...rest} />;
}

/** `dots` enciende la variante RADIO PILL · PUNTO FANCYYOOL. */
export function FkRadioGroup({ value, options, onChange, dots, disabled }) {
  return <RadioPills value={value} options={options} onChange={onChange} dots={dots} disabled={disabled} />;
}

/* ── Rail de secciones vertical (patrón "settings") ──────────────────────── */
function SideNavItem({ label, icon, count, active, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button" onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      aria-current={active ? "page" : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 11, width: "100%", textAlign: "left",
        padding: "8px 10px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
        border: "1px solid " + (active ? FK.border : "transparent"),
        background: active ? "#FFFFFF" : (h ? "#EEF0F1" : "transparent"),
        boxShadow: active ? "0 1px 2px rgba(16,38,64,.06)" : "none",
        transition: "background .15s ease, border-color .15s ease",
      }}
    >
      <span style={{
        flexShrink: 0, width: 28, height: 28, borderRadius: 8, display: "grid", placeItems: "center",
        background: active ? FK.navy : "#E7EAEC", color: active ? "#FFFFFF" : FK.sub,
        transition: "background .15s ease, color .15s ease",
      }}>
        <YIcon name={icon} size={15} />
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: active ? 600 : 500, color: active ? FK.text : FK.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.25 }}>{label}</span>
      {count != null && (
        <span style={{
          flexShrink: 0, minWidth: 20, height: 19, padding: "0 6px", borderRadius: 999,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 11.5, fontWeight: 700, fontVariantNumeric: "tabular-nums",
          background: active ? FK.navy : "#E7EAEC", color: active ? "#FFFFFF" : FK.sub,
        }}>{count}</span>
      )}
    </button>
  );
}
export function SideNav({ value, options, onChange }) {
  return (
    <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {(options || []).map((o) => (
        o.heading
          ? <div key={"h-" + o.heading} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: FK.head, padding: "14px 10px 6px" }}>{o.heading}</div>
          : <SideNavItem key={o.value} label={o.label} icon={o.icon} count={o.count} active={o.value === value} onClick={() => onChange(o.value)} />
      ))}
    </nav>
  );
}

/* ── Tarjeta + sección con hairline ──────────────────────────────────────── */
export function FormCard({ children, pad = 28, style }) {
  return <div style={{ background: FK.surface, border: "1px solid " + FK.border, borderRadius: 16, padding: pad, ...style }}>{children}</div>;
}
export function Section({ title, desc, children, first, right }) {
  const { YoolSection } = DS();
  if (YoolSection && (title || desc || right)) {
    return <YoolSection title={title} desc={desc} actions={right} divider={!first}>{children}</YoolSection>;
  }
  const wrap = first
    ? {}
    : { marginTop: 32, paddingTop: 28, borderTop: "1px solid " + FK.border };
  if (!title && !desc && !right) {
    return <div style={wrap}>{children}</div>;
  }
  return (
    <div style={wrap}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          {title && <h3 style={{ margin: 0, ...type.sectionTitle }}>{title}</h3>}
          {desc && <p style={{ margin: "6px 0 0", ...type.sectionDesc }}>{desc}</p>}
        </div>
        {right && <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>{right}</div>}
      </div>
      <div style={{ marginTop: 16 }}>{children}</div>
    </div>
  );
}
export function Row({ children, gap = 20 }) {
  return <div style={{ display: "flex", flexWrap: "wrap", gap }}>{children}</div>;
}

/* ── Pill de estado ──────────────────────────────────────────────────────── */
export function Pill({ tone = "muted", children }) {
  const map = {
    ok:     { c: FK.okText, bg: FK.okBg, dot: FK.ok },
    muted:  { c: "#5B6165", bg: "#EEF0F1", dot: "#9499A0" },
    warn:   { c: "#8A5A09", bg: FK.warnBg, dot: FK.warn },
  };
  const s = map[tone] || map.muted;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: s.c, background: s.bg, borderRadius: 999, padding: "3px 9px 3px 8px", whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {children}
    </span>
  );
}

/* ── Medidor de documentos ───────────────────────────────────────────────── */
export function DocsMeter({ done, total = 3 }) {
  const complete = done >= total;
  const c = complete ? FK.ok : (done === 0 ? FK.err : FK.warn);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} style={{ width: 22, height: 6, borderRadius: 3, background: i < done ? c : "#E6E8EA" }} />
        ))}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: c }}>
        <YIcon name={complete ? "check" : "warn"} size={15} />
        {complete ? `Completo · ${done}/${total}` : `${done}/${total} subidos`}
      </span>
    </span>
  );
}

/* ── Fila de estado (toggle + texto + pill) ──────────────────────────────── */
export function EstadoRow({ titulo, texto, on, onChange, pill }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flex: "1 1 320px", minWidth: 280, padding: "16px 18px", borderRadius: 12, border: "1px solid " + FK.border, background: "#FBFBFB" }}>
      <div style={{ paddingTop: 1 }}><FkToggle on={on} onChange={onChange} /></div>
      <div style={{ minWidth: 0, flex: 1, lineHeight: 1.4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: FK.text }}>{titulo}</span>
          {pill}
        </div>
        <p style={{ margin: "4px 0 0", fontSize: 12.5, color: FK.muted }}>{texto}</p>
      </div>
    </div>
  );
}

/* ── Fila limpia con ícono info + toggle (Plan exclusivo de empresa) ─────── */
export function InfoToggleRow({ titulo, children, on, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 18px", borderRadius: 12, border: "1px solid " + FK.border, background: "#FBFBFB" }}>
      <span style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, background: "#EAF9FC", color: FK.info, display: "grid", placeItems: "center", marginTop: 1 }}>
        <YIcon name="info" size={17} />
      </span>
      <div style={{ minWidth: 0, flex: 1, lineHeight: 1.45 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: FK.text }}>{titulo}</div>
        <p style={{ margin: "3px 0 0", fontSize: 12.5, color: FK.muted }}>{children}</p>
      </div>
      <div style={{ paddingTop: 2 }}><FkToggle on={on} onChange={onChange} /></div>
    </div>
  );
}

/* ── Barra de acciones (footer del formulario) ───────────────────────────── */
export function ActionBar({ left, right }) {
  const { YoolActionBar } = DS();
  if (YoolActionBar) return <YoolActionBar leading={left}>{right}</YoolActionBar>;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <div style={{ display: "flex", gap: 8 }}>{left}</div>
      <div style={{ display: "flex", gap: 8 }}>{right}</div>
    </div>
  );
}

/* ── Encabezado de página (back opcional + título con span coloreado) ────── */
export function PageHead({ title, sub, onBack }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {onBack && (
        <div style={{ marginBottom: 6 }}>
          <FkLink onClick={onBack}><YIcon name="back" size={16} /> Volver</FkLink>
        </div>
      )}
      {(() => {
        const { YoolPageHeader } = DS();
        return YoolPageHeader
          ? <YoolPageHeader title={title} subtitle={sub} />
          : (<>
              <h1 style={{ margin: 0, ...type.pageTitle }}>{title}</h1>
              {sub && <p style={{ margin: "6px 0 0", ...type.pageSub }}>{sub}</p>}
            </>);
      })()}
    </div>
  );
}

/* ── Encabezado de panel (título por-sección del detalle) ────────────────── */
export function PanelHeader({ title, desc }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ margin: 0, ...type.panelHeader }}>{title}</h2>
      {desc && <p style={{ margin: "4px 0 0", ...type.panelDesc }}>{desc}</p>}
    </div>
  );
}

/* ── Callout / banner (info · warn · success · neutral) ──────────────────── */
export function Callout({ tone = "info", title, children, icon }) {
  const tones = {
    info:    { bg: "#EAF6FF", border: "#CDE7FB", c: FK.sub, ic: FK.info },
    warn:    { bg: FK.warnBg, border: FK.warnBorder, c: FK.warnText, ic: FK.warn },
    success: { bg: FK.okBg, border: "#BFE3CF", c: FK.okText, ic: FK.ok },
    neutral: { bg: "#FBFBFC", border: FK.border, c: FK.sub, ic: FK.muted },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: t.bg, border: "1px solid " + t.border }}>
      <span style={{ flexShrink: 0, marginTop: 1, color: t.ic }}><YIcon name={icon || (tone === "warn" ? "warn" : "info")} size={16} /></span>
      <div style={{ minWidth: 0, fontSize: 12.5, lineHeight: 1.5, color: t.c }}>
        {title && <div style={{ fontWeight: 600, marginBottom: children ? 2 : 0 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

/* ── Tabs segmentadas (activo = card blanca; teal reservado al CTA) ───────── */
export function SegTabs({ value, options, onChange }) {
  return (
    <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 10, background: FK.gray, border: "1px solid " + FK.border }}>
      {(options || []).map((o) => {
        const active = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange && onChange(o.value)}
            style={{
              appearance: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
              padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: active ? 600 : 500,
              color: active ? FK.text : FK.sub, background: active ? FK.surface : "transparent",
              boxShadow: active ? "0 1px 2px rgba(16,38,64,.08)" : "none", transition: "all .15s ease",
            }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Checkbox con label + desc (activo = card blanca) ────────────────────── */
export function Checkbox({ id, checked, onChange, label, desc, accent }) {
  return (
    <label htmlFor={id} style={{ display: "flex", alignItems: desc ? "flex-start" : "center", gap: 11, padding: "8px 10px", borderRadius: 10, cursor: "pointer", border: "1px solid " + (checked ? FK.border : "transparent"), background: checked ? FK.surface : "transparent" }}>
      <input type="checkbox" id={id} checked={!!checked} onChange={(e) => onChange && onChange(e.target.checked, e)}
        style={{ width: 16, height: 16, accentColor: accent || FK.navy, cursor: "pointer", flexShrink: 0, marginTop: desc ? 1 : 0 }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        {label && <span style={{ display: "block", fontSize: 13.5, fontWeight: 500, color: FK.text }}>{label}</span>}
        {desc && <span style={{ display: "block", fontSize: 12, color: FK.muted }}>{desc}</span>}
      </span>
    </label>
  );
}

/* ── Input con sufijo (UF · %) ───────────────────────────────────────────── */
export function SuffixInput({ id, name, value, onChange, placeholder, suffix, type: inputType = "text", disabled }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input id={id} name={name} type={inputType} value={value ?? ""} placeholder={placeholder} disabled={disabled}
        onFocus={() => setF(true)} onBlur={() => setF(false)} onChange={(e) => onChange && onChange(e)}
        style={fieldBase(f, { paddingRight: suffix ? 44 : 12 })} />
      {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 13, fontWeight: 600, color: FK.muted }}>{suffix}</span>}
    </div>
  );
}

/* ── Editor de campo JSON (mono + validación en vivo + Formatear/Limpiar) ── */
export function JsonField({ label, name, value, onChange, hint, rows = 12, showActions = true }) {
  const [f, setF] = useState(false);
  const str = value ?? "";
  let validity = "empty";
  if (str.trim()) { try { JSON.parse(str); validity = "ok"; } catch { validity = "err"; } }
  const emit = (v) => { onChange && onChange({ target: { name, value: v } }); };
  const format = () => { try { emit(JSON.stringify(JSON.parse(str), null, 2)); } catch { /* inválido: no-op */ } };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {label && <span style={type.label}>{label}</span>}
          {validity === "ok" && <Pill tone="ok">Válido</Pill>}
          {validity === "err" && <Pill tone="warn">Revisar</Pill>}
        </div>
        {showActions && (
          <div style={{ display: "flex", gap: 4 }}>
            <FkLink onClick={format}>Formatear</FkLink>
            <FkLink onClick={() => emit("")}>Limpiar</FkLink>
          </div>
        )}
      </div>
      <textarea name={name} value={str} rows={rows}
        onFocus={() => setF(true)} onBlur={() => setF(false)} onChange={(e) => emit(e.target.value)}
        style={fieldBase(f, { height: "auto", padding: "12px 14px", ...type.mono, color: FK.text, resize: "vertical", minHeight: 120, borderColor: validity === "err" ? FK.errBorder : (f ? FK.cta : FK.border) })} />
      {hint && <p style={{ margin: 0, ...type.hint }}>{hint}</p>}
    </div>
  );
}

/* ── Divisor (hairline; opcional con etiqueta eyebrow) ───────────────────── */
export function Divider({ label }) {
  if (!label) return <div style={{ height: 1, background: FK.line, margin: "4px 0" }} />;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
      <span style={{ height: 1, flex: 1, background: FK.line }} />
      <span style={type.eyebrow}>{label}</span>
      <span style={{ height: 1, flex: 1, background: FK.line }} />
    </div>
  );
}
